import React, { useEffect } from 'react';

type Props = {
    enabled: boolean;
    companyId: string | null;
};

declare global {
    interface Window {
        klaviyo?: KlaviyoLoadedObject;
        _learnq?: unknown[][];
    }
}

export type KlaviyoQueue = unknown[][];

export type KlaviyoLoadedObject = {
    push: (args: unknown[]) => void;
    identify: (properties: KlaviyoIdentifyProperties) => void;
    track: (event: string, properties?: Record<string, unknown>) => void;
    isIdentified: () => boolean;
};

export type KlaviyoObject = KlaviyoQueue | KlaviyoLoadedObject;

export type KlaviyoIdentifyProperties = {
    $email: string;
    $first_name?: string;
    $last_name?: string;
    $phone_number?: string;
    $city?: string;
    $region?: string;
    $country?: string;
    $zip?: string;
    [key: string]: unknown;
};

export type KlaviyoViewedProductProperties = {
    ProductName: string;
    ProductID: string | number;
    SKU: string;
    Categories?: string[];
    ImageURL?: string;
    URL?: string;
    Brand?: string;
    Price?: number;
    CompareAtPrice?: number;
    Store?: string;
    Currency?: string;
};

export type KlaviyoAddedToCartProperties = KlaviyoViewedProductProperties & {
    Quantity?: number;
    Value?: number;
    RowTotal?: number;
    RowTotalIncludingTax?: number;
    CartItemID?: string | number;
    CartItemType?: string;
    ProductType?: string;
    ConfigurableOptions?: unknown[];
    BundleOptions?: unknown[];
    AddedItemProductName?: string;
    AddedItemProductID?: string | number;
    AddedItemSKU?: string;
    AddedItemCategories?: string[];
    AddedItemImageURL?: string;
    AddedItemURL?: string;
    AddedItemPrice?: number;
    AddedItemQuantity?: number;
    $value?: number;
    [key: string]: unknown;
};

/**
 * Check if Klaviyo script has loaded and initialized
 */
export function isKlaviyoLoaded(): boolean {
    return typeof window !== 'undefined' && typeof window.klaviyo?.track === 'function';
}

/**
 * Type guard to check if Klaviyo is available on window
 */
export function hasKlaviyo(): boolean {
    return typeof window !== 'undefined' && typeof window.klaviyo !== 'undefined';
}

/**
 * Get the Klaviyo loaded object (if available)
 */
export function getKlaviyo(): KlaviyoLoadedObject | undefined {
    if (hasKlaviyo()) {
        return window.klaviyo;
    }
    return undefined;
}

/**
 * Initialize Klaviyo queue (_learnq) early so events can be queued before script loads.
 * Call this as early as possible in your app.
 */
export function initKlaviyoQueue(): void {
    if (typeof window !== 'undefined') {
        window._learnq = window._learnq || [];
    }
}

/**
 * Push a command to Klaviyo.
 * Uses _learnq queue if script not loaded yet, or direct methods if available.
 */
export function pushToKlaviyo(command: unknown[]): void {
    if (typeof window === 'undefined') {
        return;
    }

    const [method, ...args] = command;

    // If Klaviyo is fully loaded, use direct methods
    if (isKlaviyoLoaded()) {
        if (method === 'track') {
            window.klaviyo!.track(args[0] as string, args[1] as Record<string, unknown>);
        } else if (method === 'identify') {
            window.klaviyo!.identify(args[0] as KlaviyoIdentifyProperties);
        }
        return;
    }

    // Script not loaded yet - push to _learnq queue
    window._learnq = window._learnq || [];
    window._learnq.push(command);
}

export const Klaviyo: React.FC<Props> = ({ enabled, companyId }) => {
    useEffect(() => {
        if (!enabled || !companyId) return undefined;

        // Queue is already initialized by initKlaviyoQueue()
        const script = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.src = `https://static.klaviyo.com/onsite/js/${companyId}/klaviyo.js`;
        document.head.appendChild(script);
        return () => script.remove();
    }, [enabled, companyId]);

    return null;
};
